/**
 * Pending-write confirmation card.
 *
 * Renders the Approve / Decline buttons for an AI-initiated write tool
 * (Privacy Policy §11). The backend captured the original user's auth
 * context when the AI called the write tool; clicking Approve POSTs the
 * confirmation token to /approve, which executes the write AS THE USER
 * in the audit trail.
 *
 * Visual states:
 *   - pending:    yellow border, both buttons enabled, countdown shown
 *   - approving:  Approve button shows spinner, Decline disabled
 *   - declining:  Decline button shows spinner, Approve disabled
 *   - approved:   green border, "Approved — done." message
 *   - declined:   gray border, "Declined." message
 *   - expired:    red border, error message + retry hint
 */
import { useEffect, useState } from 'react';

export interface PendingConfirmation {
  token: string;
  toolName: string;
  summary: string;
  expiresAt: number;
  state: 'pending' | 'approving' | 'declining' | 'approved' | 'declined' | 'expired';
  errorMessage?: string;
}

interface Props {
  confirmation: PendingConfirmation;
  onApprove: () => void;
  onDecline: () => void;
}

export function PendingConfirmationCard({ confirmation, onApprove, onDecline }: Props) {
  const { state, summary, toolName, expiresAt, errorMessage } = confirmation;

  // Countdown — re-renders every second while pending.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (state !== 'pending') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state]);

  const remainingMs = expiresAt - now;
  const remainingSec = Math.max(0, Math.floor(remainingMs / 1000));
  const expired = state === 'pending' && remainingMs <= 0;

  // Visual styling per state.
  const borderClass =
    state === 'approved' ? 'border-green-400 bg-green-50'
    : state === 'declined' ? 'border-gray-300 bg-gray-50'
    : state === 'expired' ? 'border-red-400 bg-red-50'
    : 'border-yellow-400 bg-yellow-50';

  const toolLabel = toolName.replace(/_/g, ' ');

  return (
    <div className={`mt-2 border rounded-md p-3 text-sm ${borderClass}`}>
      <div className="flex items-center gap-2 text-xs font-medium text-gray-700 uppercase tracking-wide">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" />
        </svg>
        Confirmation required: {toolLabel}
      </div>
      <div className="mt-1.5 text-sm text-gray-800">{summary}</div>

      {state === 'pending' && !expired && (
        <>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={onApprove}
              className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
            >
              Approve
            </button>
            <button
              type="button"
              onClick={onDecline}
              className="px-3 py-1.5 bg-gray-200 text-gray-800 text-xs font-medium rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Decline
            </button>
            <span className="ml-auto text-xs text-gray-500">
              expires in {remainingSec}s
            </span>
          </div>
          <div className="mt-1.5 text-xs text-gray-500">
            The AI is waiting for your confirmation. The action will execute under your identity.
          </div>
        </>
      )}

      {state === 'approving' && (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Executing...
        </div>
      )}

      {state === 'declining' && (
        <div className="mt-2 text-xs text-gray-600">Cancelling...</div>
      )}

      {state === 'approved' && (
        <div className="mt-2 text-xs font-medium text-green-700">
          Approved — done. The assistant will continue with the result.
        </div>
      )}

      {state === 'declined' && (
        <div className="mt-2 text-xs font-medium text-gray-600">
          Declined. The assistant has been notified.
        </div>
      )}

      {(state === 'expired' || expired) && (
        <div className="mt-2 text-xs text-red-700">
          {errorMessage || 'This confirmation has expired. Ask the assistant to try again.'}
        </div>
      )}
    </div>
  );
}
